// ============================================================================
// Company Info Schema — Shared across onboarding + tenant admin
// Rule 15: Single Zod schema for company validation
// ============================================================================

import { z } from 'zod';

import { TAX_NUMBER_PATTERNS } from './tax-number.validator';

/**
 * Base company info schema.
 * Country-specific tax validation added via createCompanySchema(countryCode).
 */
export const baseCompanySchema = z.object({
  companyName: z.string().min(2, 'Company name is required (min 2 characters)'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().optional(),
  website: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  taxNumber: z.string().optional(),
  logoUrl: z.string().optional(),
});

/**
 * Create a country-specific company schema with tax number validation.
 */
export function createCompanySchemaWithTax(countryCode: string) {
  const pattern = TAX_NUMBER_PATTERNS[countryCode.toUpperCase()];

  if (!pattern) {
    return baseCompanySchema;
  }

  return baseCompanySchema.extend({
    taxNumber: pattern.required
      ? z.string().regex(pattern.regex, `${pattern.label} must be in format: ${pattern.format}`)
      : z
          .string()
          .regex(pattern.regex, `${pattern.label} must be in format: ${pattern.format}`)
          .optional()
          .or(z.literal('')),
  });
}

export type CompanyInfo = z.infer<typeof baseCompanySchema>;
